import React, { useRef, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, Image, Tag, Tooltip, Button, Space, Typography } from 'antd';
import { EyeOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../utils/price';
import type { GigaProductFull } from '../types';

const { Text } = Typography;

interface VirtualProductGridProps {
  products: GigaProductFull[];
  containerHeight?: number;
  columnCount?: number;
}

// 产品卡片组件
const ProductCard: React.FC<{ product: GigaProductFull }> = ({ product }) => {
  const navigate = useNavigate();
  
  const getInventoryDisplay = (qty: number) => {
    if (qty === 0) {
      return { text: '缺货', className: 'stock-out', color: '#ff4d4f' };
    }
    if (qty < 10) {
      return { text: `仅剩 ${qty}`, className: 'stock-low', color: '#faad14' };
    }
    return { text: `${qty} 件`, className: 'stock-available', color: '#52c41a' };
  };

  const inventoryInfo = getInventoryDisplay(product.availableQty);
  const hasDiscount = product.discountedPrice && product.discountedPrice < product.price;
  const bestPrice = hasDiscount ? product.discountedPrice : product.price;

  return (
    <Card
      className="product-card"
      hoverable
      style={{ height: '100%' }}
      cover={
        <div style={{ padding: 12, background: 'rgba(0,0,0,0.2)', height: 180, borderRadius: '8px 8px 0 0' }}>
          <Image
            src={product.mainImage || 'https://via.placeholder.com/200x200?text=No+Image'}
            alt={product.productName}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            fallback="https://via.placeholder.com/200x200?text=Error"
            preview={false}
          />
        </div>
      }
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/products/${product.sku}`)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="上架到 Shopify" key="list">
          <Button
            type="text"
            size="small"
            icon={<ShoppingCartOutlined />}
            onClick={() => navigate(`/products/${product.sku}?action=list`)}
          >
            上架
          </Button>
        </Tooltip>,
      ]}
    >
      <Card.Meta
        title={
          <Tooltip title={product.productName}>
            <Text ellipsis style={{ width: '100%', fontSize: 14 }}>
              {product.productName}
            </Text>
          </Tooltip>
        }
        description={
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              SKU: {product.sku}
            </Text>
            <div>
              {hasDiscount && (
                <Tag color="red" style={{ marginRight: 4, fontSize: 10 }}>
                  促销
                </Tag>
              )}
              <span style={{ fontSize: 12, color: inventoryInfo.color }}>
                {inventoryInfo.text}
              </span>
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#ff6b6b' }}>
                {formatPrice(bestPrice || 0)}
              </span>
              {hasDiscount && (
                <span style={{ 
                  fontSize: 12, 
                  textDecoration: 'line-through', 
                  color: 'rgba(255, 255, 255, 0.35)',
                  marginLeft: 8 
                }}>
                  {formatPrice(product.price)}
                </span>
              )}
            </div>
            {product.shippingFee > 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                运费: {formatPrice(product.shippingFee)}
              </Text>
            )}
          </Space>
        }
      />
    </Card>
  );
};

/**
 * 虚拟滚动产品网格
 * 
 * 只渲染可视区域内的产品卡片，大幅提升大数据量下的性能
 */
const VirtualProductGrid: React.FC<VirtualProductGridProps> = ({
  products,
  containerHeight = 600,
  columnCount = 4,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  // 计算行数
  const rowCount = Math.ceil(products.length / columnCount);
  
  // 每行高度（卡片高度 + 间距）
  const rowHeight = 380;
  const gap = 16;

  // 虚拟化行
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight + gap,
    overscan: 2, // 预渲染上下各 2 行
  });

  // 获取某行的产品
  const getRowProducts = (rowIndex: number): GigaProductFull[] => {
    const startIndex = rowIndex * columnCount;
    return products.slice(startIndex, startIndex + columnCount);
  };

  // 计算列宽
  const columnWidth = useMemo(() => {
    return `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;
  }, [columnCount, gap]);

  return (
    <div
      ref={parentRef}
      style={{
        height: containerHeight,
        overflow: 'auto',
        contain: 'strict',
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const rowProducts = getRowProducts(virtualRow.index);
          
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${rowHeight}px`,
                transform: `translateY(${virtualRow.start}px)`,
                display: 'flex',
                gap: `${gap}px`,
              }}
            >
              {rowProducts.map((product) => (
                <div
                  key={product.sku}
                  style={{
                    width: columnWidth,
                    flexShrink: 0,
                  }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualProductGrid;
